<script setup lang="ts">
/**
 * LandingView — the persuade surface (INTENT "Landing", DESIGN §8, B7).
 *
 * Order: a two-column hero (the promise + the waitlist form, then the
 * illustration), the proof section (three real captures, light/dark per theme),
 * the band (who it's for / available now: Cairn, with its license credit),
 * a closing call to action.
 * The primary action is the email (the waitlist); the hero submit is the
 * page's one accent button at rest (DESIGN §4), so the closing form is secondary.
 *
 * Pre-launch (ADR-057): the router sends the builder home, so the builder link
 * shows only when the flag is off. Sign in lives in the nav.
 */
import { useI18n } from 'vue-i18n'
import { APP_NAME } from '@/data/app'
import heroTable1024Webp from '@/assets/imagery/hero-table-1024w.webp'
import heroTable560Webp from '@/assets/imagery/hero-table-560w.webp'
import heroTable1024Png from '@/assets/imagery/hero-table-1024w.png'
import sinceLight360 from '@/assets/imagery/landing/landing-since-light-360w.webp'
import sinceLight720 from '@/assets/imagery/landing/landing-since-light-720w.webp'
import sinceDark360 from '@/assets/imagery/landing/landing-since-dark-360w.webp'
import sinceDark720 from '@/assets/imagery/landing/landing-since-dark-720w.webp'
import postLight360 from '@/assets/imagery/landing/landing-post-light-360w.webp'
import postLight720 from '@/assets/imagery/landing/landing-post-light-720w.webp'
import postDark360 from '@/assets/imagery/landing/landing-post-dark-360w.webp'
import postDark720 from '@/assets/imagery/landing/landing-post-dark-720w.webp'
import orderLight360 from '@/assets/imagery/landing/landing-order-light-360w.webp'
import orderLight720 from '@/assets/imagery/landing/landing-order-light-720w.webp'
import orderDark360 from '@/assets/imagery/landing/landing-order-dark-360w.webp'
import orderDark720 from '@/assets/imagery/landing/landing-order-dark-720w.webp'
import { isPreLaunch } from '@/utils/prelaunch'
import WaitlistForm from '@/components/WaitlistForm.vue'

const { t } = useI18n()

/**
 * The proof section's three real captures (landing-refresh §7.4). One entry per figure, in reading
 * order; `pins` are the y of each numbered note, as a percent of the capture's height. Every scene
 * ships a light and a dark crop, and CSS on the root attribute shows the one matching the active
 * theme (`main.css` `.shot--light` / `.shot--dark`), so the other one is never fetched.
 */
const SHOTS = [
  { scene: 'since', pins: [18, 64], light: [sinceLight360, sinceLight720], dark: [sinceDark360, sinceDark720] },
  { scene: 'post', pins: [61, 74], light: [postLight360, postLight720], dark: [postDark360, postDark720] },
  { scene: 'order', pins: [13, 83], light: [orderLight360, orderLight720], dark: [orderDark360, orderDark720] },
]
</script>

<template>
  <!--
    Uses <div> (not <main>) because App.vue already owns the <main> landmark.
    Only one <main> per page for correct accessibility.
  -->
  <div class="bg-background text-fg-primary">
    <div class="mx-auto max-w-5xl px-6">
      <!-- ───────── HERO ───────── -->
      <section
        class="grid items-center gap-10 py-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-14 lg:pt-16 lg:pb-8"
      >
        <div class="flex flex-col gap-5">
          <!-- The motto is a name, not translatable copy — static text by design. -->
          <p lang="la" class="italic text-fg-secondary">Stamen et Fabula</p>
          <h1 class="text-[36px] font-bold leading-[1.15] sm:text-display">
            {{ t('hero.title') }}
          </h1>
          <p class="max-w-[34em] text-body-l text-fg-secondary">{{ t('hero.subtitle') }}</p>
          <div id="waitlist">
            <WaitlistForm />
          </div>
          <RouterLink
            v-if="!isPreLaunch"
            :to="{ name: 'builder' }"
            class="self-start text-small font-bold text-accent-text underline"
          >
            {{ t('hero.builderLink') }}
          </RouterLink>
        </div>

        <!--
          Hero image: the paused-session table (scene canon: docs/IMAGERY.md).
          Decorative — the title carries the message. Phones (≤767px) get the
          560w WebP; the <img> fallback keeps the 1024w source's intrinsic pixels,
          so its width/height stay the CLS guard (AGENTS.md image rules).
        -->
        <picture>
          <source media="(max-width: 767px)" :srcset="heroTable560Webp" type="image/webp" />
          <source :srcset="heroTable1024Webp" type="image/webp" />
          <img
            :src="heroTable1024Png"
            alt=""
            aria-hidden="true"
            width="1024"
            height="559"
            loading="eager"
            fetchpriority="high"
            class="block h-auto w-full rounded-panel"
          />
        </picture>
      </section>

      <!-- ───────── PROOF (LR-Q87 D, LR-Q88 a; §7.4) ───────── -->
      <section class="flex flex-col gap-8 py-10 lg:py-16">
        <h2 class="text-title font-bold">{{ t('proof.title') }}</h2>
        <div class="grid gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          <div v-for="s in SHOTS" :key="s.scene" class="flex flex-col gap-4">
            <h3 class="pl-7 text-heading font-bold">{{ t('proof.' + s.scene + '.title') }}</h3>
            <figure class="relative max-w-[348px] pl-7">
              <div data-proof="frame" class="overflow-hidden rounded-panel bg-surface shadow-panel">
                <img
                  class="shot--light block h-auto w-full"
                  :src="s.light[1]"
                  :srcset="s.light[0] + ' 360w, ' + s.light[1] + ' 720w'"
                  sizes="(min-width: 1024px) 281px, 342px"
                  width="720"
                  height="1040"
                  loading="lazy"
                  decoding="async"
                  :alt="t('proof.' + s.scene + '.alt')"
                />
                <img
                  class="shot--dark block h-auto w-full"
                  :src="s.dark[1]"
                  :srcset="s.dark[0] + ' 360w, ' + s.dark[1] + ' 720w'"
                  sizes="(min-width: 1024px) 281px, 342px"
                  width="720"
                  height="1040"
                  loading="lazy"
                  decoding="async"
                  :alt="t('proof.' + s.scene + '.alt')"
                />
              </div>
              <!--
                The pin: a numbered dot plus a short rule reaching the capture, in the figure's
                left gutter. A picture of a note, not a control — the list below carries it.
              -->
              <span
                v-for="(y, i) in s.pins"
                :key="i"
                data-proof="pin"
                aria-hidden="true"
                class="absolute left-0 flex items-center"
                :style="{ top: 'calc(' + y + '% - 12px)' }"
              >
                <span
                  class="flex h-6 w-6 items-center justify-center rounded-full bg-fg-primary text-small font-bold text-background tabular-nums"
                  >{{ i + 1 }}</span
                >
                <span class="h-px w-2 bg-fg-primary" />
              </span>
            </figure>
            <ol class="flex flex-col gap-2 pl-7">
              <li v-for="n in s.pins.length" :key="n" class="flex gap-3 text-fg-secondary">
                <span class="font-bold text-fg-primary tabular-nums">{{ n }}</span
                >{{ t('proof.' + s.scene + '.pin' + n) }}
              </li>
            </ol>
          </div>
        </div>
      </section>

      <!-- ───────── WHO IT'S FOR / AVAILABLE NOW ───────── -->
      <section class="grid gap-8 border-t border-border py-10 md:grid-cols-2 lg:py-14">
        <div class="flex flex-col gap-2">
          <h2 class="text-heading font-bold">{{ t('audience.title') }}</h2>
          <p class="text-fg-secondary">{{ t('audience.body') }}</p>
        </div>
        <div class="flex flex-col gap-2">
          <h2 class="text-heading font-bold">{{ t('games.title') }}</h2>
          <p class="text-fg-secondary">{{ t('games.body') }}</p>
          <p class="text-small text-fg-secondary">
            {{ t('games.credit') }}
            <a href="https://creativecommons.org/licenses/by-sa/4.0/" class="text-accent-text underline">{{
              t('games.license')
            }}</a>
          </p>
        </div>
      </section>

      <!-- ───────── CLOSING CTA ───────── -->
      <section
        class="mb-12 flex flex-col gap-6 rounded-panel bg-surface p-pad shadow-panel sm:p-8 lg:mb-16 lg:flex-row lg:items-center lg:justify-between lg:p-12"
      >
        <h2 class="text-title font-bold">{{ t('closing.title') }}</h2>
        <WaitlistForm :primary="false" />
      </section>
    </div>

    <!-- ───────── FOOTER ───────── -->
    <footer class="border-t border-border py-8">
      <div
        class="mx-auto flex max-w-5xl flex-col px-6 items-center justify-between gap-4 text-small text-fg-secondary md:flex-row"
      >
        <p>
          {{ t('footer.copyright', { year: new Date().getFullYear(), appName: APP_NAME }) }}
        </p>
        <nav aria-label="Footer links" class="flex items-center gap-4">
          <RouterLink to="/help" class="text-accent-text underline">
            {{ t('footer.help') }}
          </RouterLink>
          <RouterLink to="/help#credits" class="text-accent-text underline">
            {{ t('footer.credits') }}
          </RouterLink>
          <RouterLink to="/privacy" class="text-accent-text underline">
            {{ t('footer.privacy') }}
          </RouterLink>
        </nav>
      </div>
    </footer>
  </div>
</template>
